import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { UploadCloud, ChevronRight, CheckCircle, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { EnsembleLoader } from '@/components/ui/LoadingState'
import { cn } from '@/lib/utils'
import { DEMO_COLUMN_CLASSIFICATIONS } from '@/lib/demo/data'
import type { ColumnCategory, ColumnClassification, EchoReadiness, PIIScanResult } from '@/lib/types'
import * as XLSX from 'xlsx'

// ── XLSX multi-row header detection + merge ───────────────────────────────────

const isEmpty = (v: unknown): boolean =>
  v === null || v === undefined || String(v).trim() === ''

function detectXLSXHeaderRows(allRows: unknown[][]): number {
  if (allRows.length < 4) return 0
  const colCount = Math.max(...allRows.slice(0, 4).map(r => (r as unknown[]).length), 1)
  const row0NonEmpty = (allRows[0] as unknown[]).filter(v => !isEmpty(v)).length
  const row1NonEmpty = (allRows[1] as unknown[]).filter(v => !isEmpty(v)).length
  if (row0NonEmpty / colCount >= 0.5 || row1NonEmpty <= row0NonEmpty) return 0

  const row2Vals = (allRows[2] as unknown[]).filter(v => !isEmpty(v)).map(v => String(v).trim())
  if (row2Vals.length < 2) return 2
  const adminFraction = row2Vals.filter(v =>
    /^(answer|other|open-?ended( response)?|participant id|respondent id|response id)$/i.test(v)
  ).length / row2Vals.length
  return adminFraction > 0.3 ? 3 : 2
}

function mergeXLSXHeaderRows(allRows: unknown[][], headerRowCount: number): string[] {
  const colCount = Math.max(...allRows.slice(0, Math.min(allRows.length, headerRowCount + 2)).map(r => (r as unknown[]).length), 1)
  const clean = (v: unknown) => String(v ?? '').replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim()
  let lastR0 = ''
  let lastR1 = ''
  return Array.from({ length: colCount }, (_, i) => {
    const r0 = clean((allRows[0] as unknown[])[i])
    const r1 = headerRowCount >= 2 ? clean((allRows[1] as unknown[])[i]) : ''
    const r2 = headerRowCount >= 3 ? clean((allRows[2] as unknown[])[i]) : ''
    if (r0) lastR0 = r0
    if (r1) lastR1 = r1
    const question = lastR1 || lastR0
    const isAdmin = /^answer$/i.test(r2) || r2 === ''
    const isOther = /^other$/i.test(r2)
    const isStandalone = /^(participant id|respondent id|response id)$/i.test(r2)
    if (!question && isStandalone) return r2
    if (!question) return r2 || `col_${i + 1}`
    if (isAdmin) return question.slice(0, 200)
    if (isOther) return `${question} (Other)`.slice(0, 200)
    return `${question} — ${r2}`.slice(0, 200)
  })
}

function xlsxRowsToCsv(headers: string[], dataRows: unknown[][]): string {
  const esc = (v: unknown) => {
    const s = String(v ?? '')
    return s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')
      ? `"${s.replace(/"/g, '""')}"` : s
  }
  return [
    headers.map(esc).join(','),
    ...dataRows.map(r => Array.from({ length: headers.length }, (_, i) => esc((r as unknown[])[i])).join(',')),
  ].join('\n')
}

// ── Interfaces for confirm step ───────────────────────────────────────────────

interface SheetMeta {
  name: string
  colCount: number
  rowCount: number
  isEmpty: boolean
}

interface ConfirmData {
  filename: string
  preview_rows: Record<string, string>[]   // max 20 rows
  detected_source: 'surveymonkey' | 'qualtrics' | 'typeform' | 'googleforms' | 'excel' | 'other'
  encoding: string
  column_count: number
  row_count: number
  sheets: SheetMeta[]           // empty array for non-XLSX
  merged_headers: string[]      // non-empty if SurveyMonkey two-row detected
  classifications: ColumnClassification[]
  upload_id: string
  project_id: string
  echo_readiness?: EchoReadiness
  pii_scan_result?: PIIScanResult
}

// ── SheetPicker component ─────────────────────────────────────────────────────

function SheetPicker({ sheets, selectedSheet, onSelect }: {
  sheets: SheetMeta[]
  selectedSheet: string | null
  onSelect: (name: string) => void
}) {
  const allEmpty = sheets.every(s => s.isEmpty)
  return (
    <Card className="bg-soft-panel border-border-gray/50" padding="sm" data-testid="sheet-picker">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
        This file has multiple sheets — select one to import
      </p>
      {allEmpty ? (
        <p className="text-xs text-slate-400">No qualifying sheets found. Check that the file contains tabular data.</p>
      ) : (
        <div className="space-y-2">
          {sheets.slice(0, 20).map(sheet => {
            const isSelected = selectedSheet === sheet.name
            return (
              <button
                key={sheet.name}
                role="radio"
                tabIndex={0}
                onClick={() => !sheet.isEmpty && onSelect(sheet.name)}
                className={cn(
                  'min-h-[44px] flex items-center justify-between w-full rounded-lg px-4 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-echo-mark outline-none',
                  sheet.isEmpty
                    ? 'border border-border-gray bg-white text-slate-300 cursor-default'
                    : isSelected
                    ? 'border border-echo-indigo bg-echo-indigo/5 text-echo-indigo font-medium'
                    : 'border border-border-gray bg-white text-deep-ink hover:border-echo-indigo/40'
                )}
              >
                <span>{sheet.name}</span>
                {sheet.isEmpty ? (
                  <Badge variant="default" size="sm">Empty</Badge>
                ) : (
                  <span className="text-xs text-slate-400">
                    {sheet.colCount} columns · {sheet.rowCount.toLocaleString()} rows
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// ── TwoRowHeaderMergePanel component ─────────────────────────────────────────

function TwoRowHeaderMergePanel({ mergedHeaders, overrides, onOverrideChange }: {
  mergedHeaders: string[]
  overrides: Record<string, string>
  onOverrideChange: (overrides: Record<string, string>) => void
}) {
  const [showInputs, setShowInputs] = useState(false)
  const [savedTimestamp, setSavedTimestamp] = useState<number | null>(null)

  const handleToggle = () => {
    if (showInputs) {
      // Save
      onOverrideChange({ ...overrides })
      setSavedTimestamp(Date.now())
      setTimeout(() => setSavedTimestamp(null), 2000)
    }
    setShowInputs(v => !v)
  }

  return (
    <Card className="bg-amber-50/30 border-uncertainty-amber/40" padding="sm" data-testid="two-row-merge-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-deep-ink">Two-row headers detected and merged</p>
        <Badge variant="warning" size="sm">SurveyMonkey format</Badge>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-3">
        The question row and sub-question row have been combined into single column names. Review the merged names below.
      </p>
      {showInputs ? (
        <div className="space-y-1">
          {mergedHeaders.map((h, i) => (
            <input
              key={i}
              type="text"
              aria-label={`Column name for ${h}`}
              value={overrides[h] ?? h}
              data-testid={`merged-header-input-${i}`}
              className="h-8 text-xs px-2 border border-border-gray rounded font-mono w-full"
              onChange={e => onOverrideChange({ ...overrides, [h]: e.target.value })}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {mergedHeaders.slice(0, 8).map(h => (
            <span key={h} className="font-mono text-xs px-2 py-0.5 bg-white border border-border-gray rounded">
              {overrides[h] ?? h}
            </span>
          ))}
          {mergedHeaders.length > 8 && (
            <span className="text-xs text-slate-400">+{mergedHeaders.length - 8} more</span>
          )}
        </div>
      )}
      <div className="flex items-center gap-3 mt-2">
        <button className="text-xs text-echo-indigo hover:underline" onClick={handleToggle}>
          {showInputs ? 'Save changes' : 'Override column names manually'}
        </button>
        {savedTimestamp !== null && (
          <span className="text-xs text-validation-green">Changes saved</span>
        )}
      </div>
    </Card>
  )
}

// ── Format badge label helper ─────────────────────────────────────────────────

function getFormatBadgeLabel(detectedSource: ConfirmData['detected_source'], filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  if (detectedSource === 'surveymonkey') {
    return ext === 'xlsx' ? 'SurveyMonkey XLSX' : 'SurveyMonkey CSV'
  }
  if (detectedSource === 'qualtrics') {
    return ext === 'xlsx' ? 'Qualtrics XLSX' : 'Qualtrics CSV'
  }
  if (detectedSource === 'typeform') return 'Typeform CSV'
  if (detectedSource === 'googleforms') return 'Google Forms CSV'
  if (detectedSource === 'excel') {
    return ext === 'xls' ? 'Excel XLS' : 'Excel XLSX'
  }
  return 'CSV'
}

// ── MultiWaveImportFlow component ────────────────────────────────────────────

interface MultiWaveImportFlowProps {
  confirmData: ConfirmData | null
  uploadProjectId: string | null
  uploadId: string | null
  existingSchema: Array<{ column_name: string; category: string }>
  setExistingSchema: (schema: Array<{ column_name: string; category: string }>) => void
  columnMapping: Record<string, string | null>
  setColumnMapping: (mapping: Record<string, string | null>) => void
  linkingStrategy: 'match_by_id' | 'append'
  setLinkingStrategy: (strategy: 'match_by_id' | 'append') => void
  waveIdColumn: string | null
  setWaveIdColumn: (col: string | null) => void
  waveName: string
  setWaveName: (name: string) => void
  waveSubmitting: boolean
  setWaveSubmitting: (v: boolean) => void
  waveError: string | null
  setWaveError: (err: string | null) => void
  onCancel: () => void
  onNavigate: (to: string) => void
}

function MultiWaveImportFlow({
  confirmData,
  uploadProjectId,
  uploadId,
  existingSchema,
  setExistingSchema,
  columnMapping,
  setColumnMapping,
  linkingStrategy,
  setLinkingStrategy,
  waveIdColumn,
  setWaveIdColumn,
  waveName,
  setWaveName,
  waveSubmitting,
  setWaveSubmitting,
  waveError,
  setWaveError,
  onCancel,
  onNavigate,
}: MultiWaveImportFlowProps) {
  // Fetch existing column schema on mount
  useEffect(() => {
    if (!uploadProjectId) return
    const sessionToken = localStorage.getItem('echo_session') ?? ''
    fetch(`/api/projects/${uploadProjectId}/upload-schema`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    })
      .then(r => r.ok ? r.json() as Promise<{ columns: Array<{ column_name: string; category: string }> }> : { columns: [] })
      .then(data => {
        setExistingSchema(data.columns ?? [])
        // Auto-match columns by name (case-insensitive)
        if (confirmData?.classifications) {
          const mapping: Record<string, string | null> = {}
          for (const col of confirmData.classifications) {
            const match = data.columns.find(
              (ec: { column_name: string }) =>
                ec.column_name.toLowerCase() === col.column_name.toLowerCase()
            )
            mapping[col.column_name] = match ? match.column_name : null
          }
          setColumnMapping(mapping)
        }
      })
      .catch(() => { setExistingSchema([]) })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadProjectId])

  const newColumns = confirmData?.classifications ?? []
  const hasRespondentId = newColumns.some(c => c.category === 'respondent_id')

  const handleWaveSubmit = async () => {
    if (waveSubmitting) return
    setWaveSubmitting(true)
    setWaveError(null)

    const sessionToken = localStorage.getItem('echo_session') ?? ''
    const mappingArray = newColumns.map(col => ({
      new_col: col.column_name,
      existing_col: columnMapping[col.column_name] ?? null,
    }))

    try {
      const res = await fetch('/api/uploads/wave-merge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          project_id: uploadProjectId,
          upload_id: uploadId,
          wave_name: waveName || 'Wave 2',
          linking_strategy: linkingStrategy,
          id_column: waveIdColumn,
          column_mapping: mappingArray,
        }),
      })

      if (!res.ok) {
        const errText = await res.text().catch(() => `HTTP ${res.status}`)
        throw new Error(errText)
      }

      // Start the pipeline for the new wave
      if (uploadId && uploadProjectId) {
        try {
          const pipelineRes = await fetch(`/api/v1/uploads/${uploadId}/pipeline/start`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${sessionToken}` },
          })
          if (pipelineRes.ok) {
            const { pipeline_run_id } = await pipelineRes.json() as { pipeline_run_id: string }
            setWaveSubmitting(false)
            onNavigate(`/app/onboarding/processing?pipeline_run_id=${pipeline_run_id}&project_id=${uploadProjectId}`)
            return
          }
        } catch { /* fall through */ }
        setWaveSubmitting(false)
        onNavigate(`/app/projects/${uploadProjectId}/echo-panel`)
      }
    } catch (err) {
      setWaveError(err instanceof Error ? err.message : 'Wave merge failed')
      setWaveSubmitting(false)
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto pb-20 md:pb-6 space-y-6">
      <div>
        <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Add a new wave to this project</h1>
        <p className="text-sm text-slate-500 mt-1">Map the new file's columns to your existing data, then confirm how to link respondents.</p>
      </div>

      {/* Section 1 — Column mapping */}
      <Card padding="md">
        <p className="text-sm font-medium text-deep-ink mb-3">Column mapping</p>
        {newColumns.length === 0 ? (
          <p className="text-xs text-slate-400">No columns to map.</p>
        ) : (
          <div className="space-y-2">
            {newColumns.map(col => {
              const mappedTo = columnMapping[col.column_name] ?? null
              const existingMatch = existingSchema.find(ec => ec.column_name === mappedTo)
              const isMismatch = existingMatch && existingMatch.category !== col.category
              return (
                <div
                  key={col.column_name}
                  className={cn(
                    'flex items-center gap-3 min-h-[44px] px-2 rounded',
                    isMismatch ? 'bg-amber-50/20' : ''
                  )}
                >
                  <span className="font-mono text-xs text-slate-600 w-40 truncate">{col.column_name}</span>
                  <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" strokeWidth={1.75} />
                  {existingSchema.length > 0 ? (
                    <select
                      className="text-xs border border-border-gray rounded px-2 h-8 bg-white flex-1"
                      value={mappedTo ?? ''}
                      onChange={e => {
                        setColumnMapping({ ...columnMapping, [col.column_name]: e.target.value || null })
                      }}
                    >
                      <option value="">— New column —</option>
                      {existingSchema.map(ec => (
                        <option key={ec.column_name} value={ec.column_name}>{ec.column_name}</option>
                      ))}
                    </select>
                  ) : (
                    <Badge variant="info" size="sm">New column — will be added</Badge>
                  )}
                  <div className="shrink-0">
                    {isMismatch ? (
                      <Badge variant="warning" size="sm">Mismatch type</Badge>
                    ) : mappedTo ? (
                      <Badge variant="success" size="sm">Matched</Badge>
                    ) : (
                      <Badge variant="info" size="sm">New column</Badge>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Section 2 — Respondent linking */}
      <Card padding="md">
        <p className="text-sm font-medium text-deep-ink mb-3">Respondent linking</p>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => hasRespondentId && setLinkingStrategy('match_by_id')}
            className={cn(
              'w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors min-h-[44px]',
              !hasRespondentId
                ? 'border-border-gray bg-white opacity-40 cursor-not-allowed'
                : linkingStrategy === 'match_by_id'
                ? 'border-echo-indigo bg-echo-indigo/5'
                : 'border-border-gray bg-white hover:border-echo-indigo/40'
            )}
          >
            {linkingStrategy === 'match_by_id' && hasRespondentId ? (
              <CheckCircle className="w-4 h-4 mt-0.5 text-echo-indigo shrink-0" strokeWidth={1.75} />
            ) : (
              <div className="w-4 h-4 mt-0.5 rounded-full border-2 border-border-gray shrink-0" />
            )}
            <div>
              <p className="text-sm font-medium text-deep-ink">Match by respondent ID</p>
              <p className="text-xs text-slate-400 mt-0.5">Requires an ID column in both files (e.g. respondent_id, email)</p>
            </div>
          </button>

          {linkingStrategy === 'match_by_id' && hasRespondentId && (
            <select
              aria-label="Which column is the respondent ID?"
              className="text-xs border border-border-gray rounded px-2 h-8 bg-white mt-2 w-full"
              value={waveIdColumn ?? ''}
              onChange={e => setWaveIdColumn(e.target.value || null)}
            >
              <option value="">Select ID column…</option>
              {newColumns.map(col => (
                <option key={col.column_name} value={col.column_name}>{col.column_name}</option>
              ))}
            </select>
          )}

          <button
            type="button"
            onClick={() => setLinkingStrategy('append')}
            className={cn(
              'w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors min-h-[44px]',
              linkingStrategy === 'append'
                ? 'border-echo-indigo bg-echo-indigo/5'
                : 'border-border-gray bg-white hover:border-echo-indigo/40'
            )}
          >
            {linkingStrategy === 'append' ? (
              <CheckCircle className="w-4 h-4 mt-0.5 text-echo-indigo shrink-0" strokeWidth={1.75} />
            ) : (
              <div className="w-4 h-4 mt-0.5 rounded-full border-2 border-border-gray shrink-0" />
            )}
            <div>
              <p className="text-sm font-medium text-deep-ink">Append as new respondents</p>
              <p className="text-xs text-slate-400 mt-0.5">Respondents from the new wave are added alongside existing ones</p>
            </div>
          </button>
        </div>
      </Card>

      {/* Section 3 — Wave label */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-deep-ink shrink-0">Wave name</label>
        <input
          type="text"
          aria-label="Wave name"
          placeholder="e.g. Wave 2 — June 2026"
          value={waveName}
          onChange={e => setWaveName(e.target.value)}
          className="h-9 text-sm border border-border-gray rounded-lg px-3 flex-1"
        />
      </div>

      {/* CTA row */}
      <div className="flex flex-col gap-2">
        <Button
          variant="primary"
          data-testid="upload-confirm-btn"
          onClick={handleWaveSubmit}
          className={waveSubmitting ? 'opacity-40 cursor-not-allowed' : ''}
        >
          {waveSubmitting ? 'Adding wave…' : 'Add wave and rebuild Echo Panel'}
        </Button>
        <button
          className="text-sm text-slate-400 hover:text-deep-ink"
          onClick={onCancel}
        >
          Cancel — keep existing data
        </button>
        {waveError && <p className="text-xs text-risk-rose">{waveError}</p>}
      </div>
    </div>
  )
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS: ColumnCategory[] = [
  'respondent_id', 'demographic', 'prediction', 'open_ended',
  'high_frequency', 'numerical', 'outcome', 'ignore'
]

const CATEGORY_COLORS: Record<ColumnCategory, string> = {
  respondent_id: 'bg-evidence-blue/10 text-evidence-blue border-evidence-blue/20',
  demographic: 'bg-purple-50 text-purple-700 border-purple-200',
  prediction: 'bg-human-teal/10 text-human-teal border-human-teal/20',
  open_ended: 'bg-signal-cyan/10 text-signal-cyan border-signal-cyan/20',
  high_frequency: 'bg-orange-50 text-orange-700 border-orange-200',
  numerical: 'bg-slate-50 text-slate-600 border-slate-200',
  outcome: 'bg-validation-green/10 text-validation-green border-validation-green/20',
  ignore: 'bg-soft-panel text-slate-400 border-border-gray',
}


type Step = 'source' | 'drop' | 'parsing' | 'confirm' | 'review' | 'duplicate' | 'wave-mapping'

type SourceType = 'surveymonkey' | 'qualtrics' | 'typeform' | 'googleforms' | 'excel' | 'other'

interface SourceOption {
  id: SourceType
  label: string
  hint: string
  guide: string[]
  gotcha?: string
}

const SOURCE_OPTIONS: SourceOption[] = [
  {
    id: 'surveymonkey',
    label: 'SurveyMonkey',
    hint: 'CSV export',
    guide: [
      'Go to Analyze Results → Export All → CSV',
      'Choose "All response data" (not summary)',
      'Download the zip and use the CSV inside',
    ],
    gotcha: 'SurveyMonkey exports two header rows — Echosome merges them automatically.',
  },
  {
    id: 'qualtrics',
    label: 'Qualtrics',
    hint: 'CSV or XLSX',
    guide: [
      'Data & Analysis → Export & Import → Export Data',
      'Choose CSV or Excel, check "Use choice text"',
      'Download and upload directly',
    ],
    gotcha: 'Qualtrics adds 3 metadata rows — automatically detected and skipped.',
  },
  {
    id: 'typeform',
    label: 'Typeform',
    hint: 'CSV export',
    guide: [
      'Results → Responses → Download CSV',
      'All responses export in a single file',
      'Upload the downloaded CSV',
    ],
  },
  {
    id: 'googleforms',
    label: 'Google Forms',
    hint: 'Google Sheets → CSV',
    guide: [
      'Open the linked Google Sheet',
      'File → Download → Comma-separated values',
      'Upload the downloaded CSV',
    ],
  },
  {
    id: 'excel',
    label: 'Excel / CSV',
    hint: 'XLSX, XLS, or CSV',
    guide: [
      'Save your Excel file as .xlsx or .csv',
      'First row should contain column headers',
      'Upload directly — Echosome reads either format',
    ],
  },
  {
    id: 'other',
    label: 'Other',
    hint: 'Any tabular CSV',
    guide: [
      'Export as CSV from your survey tool',
      'First row must be column headers',
      'Upload and Echosome classifies columns automatically',
    ],
  },
]

interface DuplicateInfo {
  existing_upload_id: string
  existing_filename: string
  existing_upload_date: string
}

export function UploadPage() {
  const navigate = useNavigate()
  const { projectId: routeProjectId } = useParams<{ projectId: string }>()
  const [step, setStep] = useState<Step>('source')
  const [selectedSource, setSelectedSource] = useState<SourceType | null>(null)
  const [loaderStep, setLoaderStep] = useState(0)
  const [columns, setColumns] = useState<ColumnClassification[]>([])
  const [dragging, setDragging] = useState(false)
  const [filename, setFilename] = useState('')
  const [rowCount, setRowCount] = useState<number | null>(null)
  const [uploadProjectId, setUploadProjectId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isSimulated, setIsSimulated] = useState(false)
  const [realReadiness, setRealReadiness] = useState<EchoReadiness | null>(null)
  const [realPii, setRealPii] = useState<PIIScanResult | null>(null)
  const [selectedSegmentCols, setSelectedSegmentCols] = useState<string[]>([])
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  // Confirm step state
  const [confirmData, setConfirmData] = useState<ConfirmData | null>(null)
  const [confirmClassifications, setConfirmClassifications] = useState<ColumnClassification[]>([])
  const [sheetNames, setSheetNames] = useState<SheetMeta[]>([])
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null)
  const [columnOverrides, setColumnOverrides] = useState<Record<string, string>>({})
  // Store original XLSX ArrayBuffer so we can re-convert on sheet switch
  const [xlsxBuffer, setXlsxBuffer] = useState<ArrayBuffer | null>(null)
  const [xlsxFilename, setXlsxFilename] = useState<string | null>(null)

  // Wave-mapping step state
  const [existingSchema, setExistingSchema] = useState<Array<{ column_name: string; category: string }>>([])
  const [columnMapping, setColumnMapping] = useState<Record<string, string | null>>({})
  const [linkingStrategy, setLinkingStrategy] = useState<'match_by_id' | 'append'>('append')
  const [waveIdColumn, setWaveIdColumn] = useState<string | null>(null)
  const [waveName, setWaveName] = useState('')
  const [waveSubmitting, setWaveSubmitting] = useState(false)
  const [waveError, setWaveError] = useState<string | null>(null)

  // Sync confirmClassifications when confirmData arrives
  useEffect(() => {
    if (confirmData?.classifications) {
      setConfirmClassifications(confirmData.classifications)
    }
  }, [confirmData])

  const simulateUpload = useCallback((name: string) => {
    setFilename(name)
    setIsSimulated(true)
    setUploadProjectId(null)
    setStep('parsing')
    setLoaderStep(0)
    const steps = [0, 1, 2, 3]
    steps.forEach((s, i) => {
      setTimeout(() => {
        setLoaderStep(s + 1)
        if (i === steps.length - 1) {
          setTimeout(() => {
            const demoClassifs = DEMO_COLUMN_CLASSIFICATIONS as ColumnClassification[]
            setColumns(demoClassifs)
            setSelectedSegmentCols(demoClassifs.filter(c => c.category === 'demographic').slice(0, 3).map(c => c.column_name))
            setRowCount(1248)
            setStep('review')
          }, 800)
        }
      }, 600 * (i + 1))
    })
  }, [])

  const realUpload = useCallback(async (file: File, currentSheetNames?: SheetMeta[]) => {
    setFilename(file.name)
    setIsSimulated(false)
    setUploadError(null)
    setStep('parsing')
    setLoaderStep(1)

    const sessionToken = localStorage.getItem('echo_session')
    if (!sessionToken) {
      simulateUpload(file.name)
      return
    }

    // Resolve project ID: URL param → query param → fetch latest from API
    let projectId = routeProjectId ?? new URLSearchParams(window.location.search).get('project_id') ?? null

    if (!projectId || projectId === 'demo') {
      // No project in URL — fetch the user's most recent project
      try {
        const projRes = await fetch('/api/projects', { headers: { Authorization: `Bearer ${sessionToken}` } })
        if (projRes.ok) {
          const projects = await projRes.json() as Array<{ project_id: string }>
          if (projects.length > 0) projectId = projects[0].project_id
        }
      } catch { /* ignore — will fall through to error */ }
    }

    if (!projectId || projectId === 'demo') {
      setUploadError('No project found. Create a project first or complete onboarding.')
      simulateUpload(file.name)
      return
    }

    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('project_id', projectId)
      // Pass source hint so worker can map 'googleforms' properly (parser detects 'standard')
      fd.append('source_hint', selectedSource ?? 'other')

      setLoaderStep(2)
      const res = await fetch('/api/uploads', {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}` },
        body: fd,
      })

      setLoaderStep(3)
      if (!res.ok) {
        const errText = await res.text().catch(() => `HTTP ${res.status}`)
        throw new Error(errText)
      }
      const data = await res.json() as {
        duplicate?: boolean
        existing_upload_id?: string
        existing_filename?: string
        existing_upload_date?: string
        classifications?: ColumnClassification[]
        row_count?: number
        upload_id?: string
        echo_readiness?: EchoReadiness
        pii_scan_result?: PIIScanResult
        // New confirm-step fields
        preview_rows?: Record<string, string>[]
        detected_source?: ConfirmData['detected_source']
        encoding?: string
        column_count?: number
        merged_headers?: string[]
        is_second_upload?: boolean
        sheets?: SheetMeta[]
      }

      // Duplicate detected — show confirmation before proceeding
      if (data.duplicate) {
        setDuplicateInfo({
          existing_upload_id: data.existing_upload_id ?? '',
          existing_filename: data.existing_filename ?? filename,
          existing_upload_date: data.existing_upload_date ?? '',
        })
        setPendingFile(file)
        setUploadProjectId(projectId)
        setStep('duplicate')
        return
      }

      setLoaderStep(4)
      const classifs = data.classifications ?? DEMO_COLUMN_CLASSIFICATIONS as ColumnClassification[]
      setColumns(classifs)
      setSelectedSegmentCols(classifs.filter(c => c.category === 'demographic').slice(0, 3).map(c => c.column_name))
      setRowCount(data.row_count ?? null)
      setUploadProjectId(projectId)
      if (data.upload_id) setUploadId(data.upload_id)

      if (data.echo_readiness) {
        setRealReadiness(data.echo_readiness)
      } else {
        // Derive readiness from column classifications
        const hasDemographic = classifs.some(c => c.category === 'demographic')
        const hasOutcome = classifs.some(c => c.category === 'outcome')
        const hasOpenEnded = classifs.some(c => c.category === 'open_ended')
        const total = data.row_count ?? 0
        setRealReadiness({
          overall: hasOutcome && total > 100 ? 'Medium' : 'Low',
          response_volume: total > 500 ? 'High' : total > 100 ? 'Medium' : 'Low',
          open_ended_depth: hasOpenEnded ? 'Medium' : 'Low',
          segment_coverage: hasDemographic ? 'Medium' : 'Low',
          repeated_respondents: false,
          recency: 'Unknown',
          source_diversity: 'Low',
          outcome_present: hasOutcome,
          consent_present: classifs.some(c => /consent|gdpr|opt/i.test(c.column_name)),
          available_modes: hasOutcome ? ['study_memory', 'segment_echo'] : ['study_memory'],
          limitations: [
            ...(!hasOutcome ? ['No outcome column detected — prediction-mode echoes unavailable'] : []),
            ...(!hasOpenEnded ? ['No open-ended column detected — depth signals will be limited'] : []),
          ],
        })
      }

      // Use server-returned pii_scan_result (per D-G3); fall back to null if absent
      if (data.pii_scan_result) {
        setRealPii(data.pii_scan_result)
      }

      // If server returns preview data → go to confirm step
      if (data.preview_rows !== undefined) {
        // Map detected_source: if server returns 'other' and we have a source_hint, use hint
        const resolvedSource: ConfirmData['detected_source'] =
          data.detected_source && data.detected_source !== 'other'
            ? data.detected_source
            : (selectedSource as ConfirmData['detected_source'] | null) ?? 'other'

        setConfirmData({
          filename: file.name,
          preview_rows: data.preview_rows ?? [],
          detected_source: resolvedSource,
          encoding: data.encoding ?? 'UTF-8',
          column_count: data.column_count ?? classifs.length,
          row_count: data.row_count ?? 0,
          sheets: currentSheetNames ?? data.sheets ?? [],
          merged_headers: data.merged_headers ?? [],
          classifications: classifs,
          upload_id: data.upload_id ?? '',
          project_id: projectId,
          echo_readiness: data.echo_readiness,
          pii_scan_result: data.pii_scan_result,
        })

        // Route to confirm or wave-mapping
        if (data.is_second_upload) {
          setStep('wave-mapping')
        } else {
          setStep('confirm')
        }
      } else {
        // Fallback: server omits preview_rows — build synthetic preview from classifications
        // and always route to confirm so the user sees the recognition step (per D-G1)
        const resolvedSource: ConfirmData['detected_source'] =
          data.detected_source && data.detected_source !== 'other'
            ? data.detected_source
            : (selectedSource as ConfirmData['detected_source'] | null) ?? 'other'

        setConfirmData({
          filename: file.name,
          preview_rows: [],
          detected_source: resolvedSource,
          encoding: data.encoding ?? 'UTF-8',
          column_count: data.column_count ?? classifs.length,
          row_count: data.row_count ?? 0,
          sheets: currentSheetNames ?? data.sheets ?? [],
          merged_headers: data.merged_headers ?? [],
          classifications: classifs,
          upload_id: data.upload_id ?? '',
          project_id: projectId,
          echo_readiness: data.echo_readiness,
          pii_scan_result: data.pii_scan_result,
        })

        if (data.is_second_upload) {
          setStep('wave-mapping')
        } else {
          setStep('confirm')
        }
      }
    } catch (err) {
      console.error('Upload error:', err)
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
      // Transition back to drop step so the error is visible in the file-drop UI
      setStep('drop')
    }
  }, [routeProjectId, simulateUpload, selectedSource])

  // Convert XLSX/XLS to CSV before upload; gather all sheet metadata first
  const prepareFile = useCallback(async (file: File): Promise<File> => {
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (ext !== 'xlsx' && ext !== 'xls') return file

    const buf = await file.arrayBuffer()
    // Store buffer so we can re-convert on sheet switch
    setXlsxBuffer(buf)
    setXlsxFilename(file.name)

    const wb = XLSX.read(buf, { type: 'array' })

    // Gather sheet metadata for all sheets
    // Use sheet_to_json to count actual rows — avoids false-empty when !ref is null/undefined
    const allSheetMeta: SheetMeta[] = wb.SheetNames.map(name => {
      const ws = wb.Sheets[name]
      const probe = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null }) as unknown[][]
      const hRows = detectXLSXHeaderRows(probe)
      const skipRows = Math.max(1, hRows)
      const sheetIsEmpty = probe.length <= skipRows
      const rowCount = Math.max(0, probe.length - skipRows)
      const colCount = Math.max(...probe.slice(0, Math.min(probe.length, 4)).map(r => (r as unknown[]).length), 0)
      return { name, colCount, rowCount, isEmpty: sheetIsEmpty }
    })

    setSheetNames(allSheetMeta)

    // Pre-select first non-empty sheet
    const firstNonEmpty = allSheetMeta.find(s => !s.isEmpty)
    const targetSheet = firstNonEmpty?.name ?? wb.SheetNames[0]
    setSelectedSheet(targetSheet)

    // Convert selected sheet to CSV, merging multi-row headers when detected
    const ws = wb.Sheets[targetSheet]
    const allRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null }) as unknown[][]
    const headerRowCount = detectXLSXHeaderRows(allRows)
    const csv = headerRowCount > 0
      ? xlsxRowsToCsv(mergeXLSXHeaderRows(allRows, headerRowCount), allRows.slice(headerRowCount))
      : XLSX.utils.sheet_to_csv(ws)
    const csvName = file.name.replace(/\.(xlsx|xls)$/i, '.csv')
    return new File([csv], csvName, { type: 'text/csv' })
  }, [])

  // Re-convert XLSX when sheet selection changes
  const handleSheetSelect = useCallback(async (sheetName: string) => {
    if (!xlsxBuffer || !xlsxFilename) return
    setSelectedSheet(sheetName)

    const wb = XLSX.read(xlsxBuffer, { type: 'array' })
    const ws = wb.Sheets[sheetName]
    const allRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null }) as unknown[][]
    const headerRowCount = detectXLSXHeaderRows(allRows)
    const csv = headerRowCount > 0
      ? xlsxRowsToCsv(mergeXLSXHeaderRows(allRows, headerRowCount), allRows.slice(headerRowCount))
      : XLSX.utils.sheet_to_csv(ws)
    const csvName = xlsxFilename.replace(/\.(xlsx|xls)$/i, '.csv')
    const csvFile = new File([csv], csvName, { type: 'text/csv' })

    // Re-upload the new sheet to get fresh preview
    void realUpload(csvFile, sheetNames)
  }, [xlsxBuffer, xlsxFilename, realUpload, sheetNames])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) void prepareFile(file).then(f => realUpload(f))
  }, [realUpload, prepareFile])

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) void prepareFile(file).then(f => realUpload(f))
  }, [realUpload, prepareFile])

  const useDemoData = () => simulateUpload('insurance-renewal-survey.csv')

  // ── Source picker step ────────────────────────────────────────────────────

  if (step === 'source') {
    const active = selectedSource ? SOURCE_OPTIONS.find(s => s.id === selectedSource) : null
    return (
      <div className="p-6 max-w-2xl mx-auto pb-20 md:pb-6 space-y-6">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Where is your data coming from?</h1>
          <p className="text-sm text-slate-500 mt-1">Pick the source so we can apply the right import settings.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {SOURCE_OPTIONS.map(opt => (
            <button
              key={opt.id}
              data-testid={`upload-source-tile-${opt.id}`}
              onClick={() => setSelectedSource(s => s === opt.id ? null : opt.id)}
              className={cn(
                'flex flex-col items-start p-4 rounded-xl border text-left transition-colors',
                selectedSource === opt.id
                  ? 'border-echo-indigo bg-echo-indigo/5 text-echo-indigo'
                  : 'border-border-gray bg-white hover:border-echo-indigo/40 text-deep-ink'
              )}
            >
              {selectedSource === opt.id && (
                <CheckCircle className="w-4 h-4 mb-2 text-echo-indigo" strokeWidth={1.75} />
              )}
              {selectedSource !== opt.id && (
                <div className="w-4 h-4 mb-2 rounded-full border-2 border-border-gray" />
              )}
              <span className="text-sm font-medium">{opt.label}</span>
              <span className="text-xs text-slate-400 mt-0.5">{opt.hint}</span>
            </button>
          ))}
        </div>

        {active && (
          <Card className="bg-echo-indigo/5 border-echo-indigo/20 space-y-3">
            <p className="text-xs font-medium text-echo-indigo uppercase tracking-wide">
              How to export from {active.label}
            </p>
            <ol className="space-y-1.5">
              {active.guide.map((guideStep, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-deep-ink">
                  <span className="w-5 h-5 rounded-full bg-echo-indigo/10 text-echo-indigo text-xs font-medium flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  {guideStep}
                </li>
              ))}
            </ol>
            {active.gotcha && (
              <p className="text-xs text-uncertainty-amber flex items-center gap-1.5">
                <span>Note:</span> {active.gotcha}
              </p>
            )}
          </Card>
        )}

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            onClick={() => setStep('drop')}
            disabled={!selectedSource}
            className="gap-1.5"
          >
            Continue
            <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
          </Button>
          <button
            onClick={() => { setSelectedSource('other'); setStep('drop') }}
            className="text-sm text-slate-400 hover:text-deep-ink"
          >
            Skip — just upload a file
          </button>
        </div>
      </div>
    )
  }

  // ── Parsing step ──────────────────────────────────────────────────────────

  if (step === 'parsing') {
    return (
      <div className="p-6 max-w-xl mx-auto pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink mb-6">Uploading study…</h1>
        <Card>
          <EnsembleLoader step={loaderStep} total={5} />
          <p className="text-xs text-center text-slate-400">{filename}</p>
        </Card>
      </div>
    )
  }

  // ── Duplicate step ────────────────────────────────────────────────────────

  if (step === 'duplicate' && duplicateInfo) {
    const uploadDate = duplicateInfo.existing_upload_date
      ? new Date(duplicateInfo.existing_upload_date).toLocaleDateString()
      : 'previously'
    return (
      <div className="p-6 max-w-xl mx-auto pb-20 md:pb-6" data-testid="duplicate-upload-dialog">
        <h1 className="text-2xl font-medium text-deep-ink mb-2">File already uploaded</h1>
        <p className="text-sm text-slate-500 mb-6">
          <strong>{duplicateInfo.existing_filename}</strong> was already successfully processed on {uploadDate}. Re-upload only if you want to replace it.
        </p>
        <Card className="space-y-3 p-5">
          <Button
            className="w-full"
            onClick={() => {
              if (uploadProjectId) {
                navigate(`/app/projects/${uploadProjectId}/echo-panel`)
              }
            }}
          >
            Use existing upload
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            onClick={async () => {
              if (!pendingFile || !uploadProjectId) return
              setStep('parsing')
              setLoaderStep(2)
              const sessionToken = localStorage.getItem('echo_session') ?? ''
              const fd = new FormData()
              fd.append('file', pendingFile)
              fd.append('project_id', uploadProjectId)
              fd.append('force_replace', '1')
              try {
                const res = await fetch('/api/uploads', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${sessionToken}` },
                  body: fd,
                })
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                const data = await res.json() as { upload_id?: string; classifications?: ColumnClassification[]; row_count?: number; echo_readiness?: EchoReadiness; pii_scan_result?: PIIScanResult }
                setLoaderStep(4)
                const classifs = data.classifications ?? []
                setColumns(classifs)
                setSelectedSegmentCols(classifs.filter(c => c.category === 'demographic').slice(0, 3).map(c => c.column_name))
                setRowCount(data.row_count ?? null)
                if (data.upload_id) setUploadId(data.upload_id)
                if (data.echo_readiness) setRealReadiness(data.echo_readiness)
                setStep('review')
              } catch (err) {
                setUploadError(err instanceof Error ? err.message : 'Upload failed')
                setStep('drop')
              }
            }}
          >
            Re-process with this file
          </Button>
          <button
            className="w-full text-sm text-slate-400 hover:text-slate-600 py-2"
            onClick={() => { setStep('drop'); setDuplicateInfo(null); setPendingFile(null) }}
          >
            Cancel
          </button>
        </Card>
      </div>
    )
  }

  // ── Confirm step ──────────────────────────────────────────────────────────

  if (step === 'confirm' && confirmData) {
    const formatBadgeLabel = getFormatBadgeLabel(confirmData.detected_source, confirmData.filename)
    const previewHeaders = confirmData.preview_rows.length > 0
      ? Object.keys(confirmData.preview_rows[0])
      : []

    async function handleConfirm() {
      const sessionToken = localStorage.getItem('echo_session') ?? ''

      // Apply any column classification overrides first (non-blocking)
      if (uploadId && confirmClassifications.length > 0) {
        try {
          await fetch(`/api/v1/uploads/${uploadId}/columns`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({
              classifications: confirmClassifications.map(c => ({
                column_name: c.column_name,
                category: c.category,
              })),
            }),
          })
        } catch {
          // Non-blocking — pipeline can still start with original classifications
        }
      }

      // Start the pipeline immediately and navigate to project home
      if (uploadId && uploadProjectId) {
        try {
          const res = await fetch(`/api/v1/uploads/${uploadId}/pipeline/start`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${sessionToken}` },
          })
          if (res.ok) {
            navigate(`/app/projects/${uploadProjectId}`, { replace: true })
            return
          }
        } catch { /* fall through */ }
      }

      // Fallback: go to review step so user can manually start
      setStep('review')
    }

    return (
      <div className="p-6 max-w-xl mx-auto pb-20 md:pb-6" data-testid="confirm-recognition-card">
        <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Review your file before importing</h1>
        <p className="text-sm text-slate-500 mt-1">
          {confirmData.filename} · {confirmData.column_count} columns · {confirmData.row_count.toLocaleString()} rows
        </p>

        <div className="space-y-4 mt-6">
          {/* Filename + badges */}
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-mono text-xs text-slate-600">{confirmData.filename}</p>
            <Badge variant="info" size="sm" aria-label={`Detected format: ${formatBadgeLabel}`}>{formatBadgeLabel}</Badge>
            <Badge variant="default" size="sm">{confirmData.encoding}</Badge>
          </div>

          {/* Column / row counts */}
          <div className="flex gap-4">
            <span className="text-sm text-deep-ink font-medium">{confirmData.column_count} columns</span>
            <span className="text-sm text-deep-ink font-medium">{confirmData.row_count.toLocaleString()} rows</span>
          </div>

          {/* SheetPicker — rendered before TwoRowHeaderMergePanel per UI-SPEC */}
          {sheetNames.length > 1 && (
            <SheetPicker
              sheets={sheetNames}
              selectedSheet={selectedSheet}
              onSelect={handleSheetSelect}
            />
          )}

          {/* TwoRowHeaderMergePanel */}
          {confirmData.merged_headers.length > 0 && (
            <TwoRowHeaderMergePanel
              mergedHeaders={confirmData.merged_headers}
              overrides={columnOverrides}
              onOverrideChange={setColumnOverrides}
            />
          )}

          {/* Preview table */}
          <Card padding="none" border={true}>
            {confirmData.preview_rows.length === 0 ? (
              <p className="text-xs text-slate-400 italic p-4">Preview not available for this file type</p>
            ) : (
              <div className="overflow-x-auto">
                <table role="table" className="w-full">
                  <thead>
                    <tr>
                      {previewHeaders.map(h => (
                        <th
                          key={h}
                          scope="col"
                          className="text-xs font-medium text-slate-400 border-b border-border-gray px-3 py-2 min-w-[120px] max-w-[200px] text-left"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {confirmData.preview_rows.slice(0, 20).map((row, i) => (
                      <tr key={i}>
                        {previewHeaders.map(h => (
                          <td
                            key={h}
                            className="h-8 text-xs font-mono text-slate-600 border-b border-border-gray/50 px-3 truncate max-w-[200px]"
                          >
                            {row[h] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* PII scan summary (confirm step) */}
          {confirmData.pii_scan_result && confirmData.pii_scan_result.pii_columns.length > 0 && (
            <Card className="border-uncertainty-amber/40 bg-amber-50/30">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-sm font-medium text-deep-ink">Data sensitivity</h3>
                <Badge variant="warning" size="sm">
                  {confirmData.pii_scan_result.pii_columns.length} PII column{confirmData.pii_scan_result.pii_columns.length > 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <span className="text-uncertainty-amber shrink-0">!</span>
                <p className="text-slate-600">PII detected in {confirmData.pii_scan_result.pii_columns.length} column{confirmData.pii_scan_result.pii_columns.length > 1 ? 's' : ''}:
                  {confirmData.pii_scan_result.pii_columns.map(c => (
                    <span key={c.column_name} data-testid={`pii-flagged-column-${c.column_name}`} className="ml-1 font-mono text-xs bg-white border border-border-gray rounded px-1">{c.column_name} ({c.pii_type})</span>
                  ))}
                </p>
              </div>
            </Card>
          )}

          {/* Column roles — adjust before building */}
          {confirmClassifications.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-deep-ink mb-2">
                Column roles
                <span className="text-slate-400 font-normal ml-1">— adjust before building</span>
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border-gray text-xs text-slate-400 text-left">
                      <th className="pb-2 pr-3 font-medium">Column</th>
                      <th className="pb-2 font-medium">Role</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-gray">
                    {confirmClassifications.map(col => (
                      <tr key={col.column_name}>
                        <td className="py-2 pr-3 font-mono text-xs text-slate-600">{col.column_name}</td>
                        <td className="py-2">
                          <select
                            data-testid={`column-role-select-${col.column_name}`}
                            className="text-xs px-2 py-1 rounded-full border font-medium cursor-pointer bg-transparent border-border-gray text-deep-ink"
                            value={col.category}
                            onChange={e => setConfirmClassifications(prev =>
                              prev.map(c => c.column_name === col.column_name
                                ? { ...c, category: e.target.value as ColumnCategory }
                                : c
                              )
                            )}
                          >
                            {CATEGORY_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CTA row */}
          <div className="flex flex-col gap-2 mt-2">
            <Button variant="primary" onClick={handleConfirm} data-testid="upload-confirm-btn">
              Confirm — build Echo Panel
            </Button>
            <button
              className="text-sm text-slate-400 hover:text-deep-ink"
              onClick={() => setStep('drop')}
            >
              Back to file upload
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Wave-mapping step ─────────────────────────────────────────────────────

  if (step === 'wave-mapping') {
    return (
      <div data-testid="confirm-recognition-card">
        <MultiWaveImportFlow
          confirmData={confirmData}
          uploadProjectId={uploadProjectId}
          uploadId={uploadId}
          existingSchema={existingSchema}
          setExistingSchema={setExistingSchema}
          columnMapping={columnMapping}
          setColumnMapping={setColumnMapping}
          linkingStrategy={linkingStrategy}
          setLinkingStrategy={setLinkingStrategy}
          waveIdColumn={waveIdColumn}
          setWaveIdColumn={setWaveIdColumn}
          waveName={waveName}
          setWaveName={setWaveName}
          waveSubmitting={waveSubmitting}
          setWaveSubmitting={setWaveSubmitting}
          waveError={waveError}
          setWaveError={setWaveError}
          onCancel={() => setStep('drop')}
          onNavigate={navigate}
        />
      </div>
    )
  }

  // ── Review step ───────────────────────────────────────────────────────────

  if (step === 'review') {
    const destProjectId = uploadProjectId ?? routeProjectId ?? 'demo'
    const echoPath = `/app/projects/${destProjectId}/echo-panel`
    const readiness = isSimulated ? null : realReadiness
    const pii = isSimulated ? null : realPii

    return (
      <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Review columns</h1>
            <p className="text-sm text-slate-500 mt-1">
              {filename} · {rowCount !== null ? `${rowCount.toLocaleString()} rows detected` : 'rows detected'}
              {isSimulated && !uploadError && <span className="ml-2 text-slate-400 text-xs">(demo preview)</span>}
              {isSimulated && uploadError && <span className="ml-2 text-uncertainty-amber text-xs">(demo preview — real upload failed)</span>}
            </p>
            {uploadError && <p className="text-xs text-risk-rose mt-1">Error: {uploadError}</p>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setStep('source'); setSelectedSource(null) }}>Start over</Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (uploadId && uploadProjectId && !isSimulated) {
                  try {
                    const sessionToken = localStorage.getItem('echo_session')
                    const res = await fetch(`/api/v1/uploads/${uploadId}/pipeline/start`, {
                      method: 'POST',
                      headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
                    })
                    if (res.ok) {
                      const { pipeline_run_id } = await res.json() as { pipeline_run_id: string }
                      navigate(`/app/onboarding/processing?pipeline_run_id=${pipeline_run_id}&project_id=${uploadProjectId}`)
                      return
                    }
                  } catch { /* fall through to echo-panel */ }
                }
                navigate(echoPath)
              }}
            >
              Confirm & Build Echo Panel →
            </Button>
          </div>
        </div>

        {/* PII Scan */}
        <Card className="border-uncertainty-amber/40 bg-amber-50/30">
          {pii === null ? (
            <div className="text-sm text-slate-500 italic">PII scan not yet available</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="text-sm font-medium text-deep-ink">Data sensitivity</h3>
                <Badge variant={pii.safe_for_demo || pii.pii_columns.length === 0 ? 'success' : 'warning'} size="sm">
                  {pii.pii_columns.length === 0 ? 'No PII detected' : isSimulated ? 'Safe for demo' : `${pii.pii_columns.length} PII column${pii.pii_columns.length > 1 ? 's' : ''}`}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                {pii.pii_columns.length > 0 ? (
                  <div className="flex items-start gap-2">
                    <span className="text-uncertainty-amber shrink-0">!</span>
                    <p className="text-slate-600">PII detected in {pii.pii_columns.length} column{pii.pii_columns.length > 1 ? 's' : ''}:
                      {pii.pii_columns.map(c => (
                        <span key={c.column_name} data-testid={`pii-flagged-column-${c.column_name}`} className="ml-1 font-mono text-xs bg-white border border-border-gray rounded px-1">{c.column_name} ({c.pii_type})</span>
                      ))}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <span className="text-validation-green shrink-0">+</span>
                    <p className="text-slate-600">No PII columns detected</p>
                  </div>
                )}
                {!pii.consent_present && (
                  <div className="flex items-start gap-2">
                    <span className="text-uncertainty-amber shrink-0">!</span>
                    <p className="text-slate-600">Consent metadata not found (no consent/opted_in/gdpr column)</p>
                  </div>
                )}
                {isSimulated && (
                  <div className="flex items-start gap-2">
                    <span className="text-validation-green shrink-0">+</span>
                    <p className="text-slate-600">Demo mode: data processed locally, never sent to external models</p>
                  </div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-uncertainty-amber/20">
                <Button variant="ghost" size="sm" className="text-risk-rose text-xs">Delete this data</Button>
              </div>
            </>
          )}
        </Card>

        {/* Echo Readiness */}
        <Card>
          {readiness === null ? (
            <div className="text-sm text-slate-500 italic">Analyzing your data...</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-deep-ink">Echo Readiness Score</h3>
                <Badge variant={readiness.overall === 'High' ? 'success' : readiness.overall === 'Medium' ? 'warning' : 'danger'}>{readiness.overall}</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                {[
                  { label: 'Response volume', value: readiness.response_volume },
                  { label: 'Open-ended depth', value: readiness.open_ended_depth },
                  { label: 'Segment coverage', value: readiness.segment_coverage },
                  { label: 'Recency', value: readiness.recency },
                  { label: 'Source diversity', value: readiness.source_diversity },
                  { label: 'Outcome column', value: readiness.outcome_present ? 'Present' : 'Missing' },
                ].map(item => (
                  <div key={item.label} className="text-xs">
                    <p className="text-slate-400 mb-0.5">{item.label}</p>
                    <Badge
                      variant={item.value === 'High' || item.value === 'Present' ? 'success' : item.value === 'Medium' || item.value === 'Unknown' ? 'warning' : 'danger'}
                      size="sm"
                    >{item.value}</Badge>
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-border-gray">
                <p className="text-xs text-slate-500 mb-1">Available echo modes:</p>
                <div className="flex flex-wrap gap-1">
                  {readiness.available_modes.map(mode => (
                    <Badge key={mode} variant="default" size="sm">{mode.replace(/_/g, ' ')}</Badge>
                  ))}
                </div>
              </div>
              {readiness.limitations.map((lim, i) => (
                <p key={i} className="text-xs text-slate-400 mt-2 italic">{lim}</p>
              ))}
            </>
          )}
        </Card>

        {/* Column classifications */}
        <div>
          <h3 className="text-sm font-medium text-deep-ink mb-3">Column classifications <span className="text-slate-400 font-normal">— click any category to override</span></h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-gray text-xs text-slate-400 text-left">
                  <th className="pb-2 pr-3 font-medium">Column</th>
                  <th className="pb-2 pr-3 font-medium">Category</th>
                  <th className="pb-2 pr-3 font-medium">Confidence</th>
                  <th className="pb-2 font-medium">Sample values</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-gray">
                {columns.map(col => (
                  <tr key={col.column_name}>
                    <td className="py-2.5 pr-3 font-mono text-xs text-slate-600">{col.column_name}</td>
                    <td className="py-2.5 pr-3">
                      <select
                        data-testid={`column-role-select-${col.column_name}`}
                        className={cn(
                          'text-xs px-2 py-1 rounded-full border font-medium cursor-pointer bg-transparent',
                          CATEGORY_COLORS[col.category]
                        )}
                        defaultValue={col.category}
                        onChange={e => {
                          const updated = columns.map(c =>
                            c.column_name === col.column_name
                              ? { ...c, category: e.target.value as ColumnCategory }
                              : c
                          )
                          setColumns(updated)
                        }}
                      >
                        {CATEGORY_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2.5 pr-3">
                      <Badge
                        variant={col.confidence === 'High' ? 'success' : col.confidence === 'Medium' ? 'warning' : 'danger'}
                        size="sm"
                      >{col.confidence}</Badge>
                    </td>
                    <td className="py-2.5">
                      <span className="text-xs text-slate-400">{col.sample_values?.slice(0, 2).join(', ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Segment mapping */}
        <Card className="bg-soft-panel border-0">
          <h3 className="text-sm font-medium text-deep-ink mb-1">Map segments</h3>
          <p className="text-xs text-slate-500 mb-3">
            Select 1–3 columns to define segments for Echo Panel construction.
            {selectedSegmentCols.length > 0 && <span className="ml-1 text-human-teal">{selectedSegmentCols.length} selected.</span>}
          </p>
          <div className="flex flex-wrap gap-2">
            {columns
              .filter(c => c.category !== 'ignore' && c.category !== 'respondent_id')
              .map(col => {
                const isSelected = selectedSegmentCols.includes(col.column_name)
                return (
                  <button
                    key={col.column_name}
                    type="button"
                    onClick={() => {
                      setSelectedSegmentCols(prev =>
                        isSelected
                          ? prev.filter(n => n !== col.column_name)
                          : prev.length < 3 ? [...prev, col.column_name] : prev
                      )
                    }}
                    className={cn(
                      'text-xs px-3 py-1.5 rounded-full border font-medium transition-colors',
                      isSelected
                        ? 'border-human-teal text-human-teal bg-human-teal/5'
                        : 'border-border-gray text-slate-500 bg-white hover:border-human-teal/50'
                    )}
                  >
                    {col.column_name}
                    <span className="ml-1 opacity-50 capitalize">{col.category.replace(/_/g, ' ')}</span>
                  </button>
                )
              })}
          </div>
          {selectedSegmentCols.length === 3 && (
            <p className="text-xs text-slate-400 mt-2">Maximum 3 segment columns selected. Deselect one to change.</p>
          )}
        </Card>
      </div>
    )
  }

  // ── Drop zone ─────────────────────────────────────────────────────────────

  const activeSource = selectedSource ? SOURCE_OPTIONS.find(s => s.id === selectedSource) : null
  return (
    <div className="p-6 space-y-6 max-w-2xl pb-20 md:pb-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Upload your file</h1>
          {activeSource ? (
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="info" size="sm">{activeSource.label}</Badge>
              <button onClick={() => setStep('source')} className="text-xs text-slate-400 hover:text-deep-ink">Change source</button>
            </div>
          ) : (
            <p className="text-sm text-slate-500 mt-1">CSV, XLSX, XLS, or any tabular file. Columns are auto-classified.</p>
          )}
        </div>
      </div>

      <div
        data-testid="upload-dropzone"
        className={cn(
          'border-2 border-dashed rounded-2xl p-12 text-center transition-colors',
          dragging ? 'border-human-teal bg-human-teal/5' : 'border-border-gray hover:border-human-teal/50'
        )}
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <UploadCloud className="w-10 h-10 text-echo-mark mx-auto mb-4" strokeWidth={1.5} />
        <p className="text-sm font-medium text-deep-ink mb-1">Drop your file here</p>
        <p className="text-xs text-slate-400 mb-4">CSV, TXT, JSON, Markdown, PDF</p>
        <label className="cursor-pointer">
          <input type="file" className="sr-only" accept=".csv,.xlsx,.xls,.txt,.json,.md,.pdf" onChange={onFileChange} />
          <Button variant="outline" size="sm" className="pointer-events-none">Browse files</Button>
        </label>
      </div>

      {uploadError && (
        <div
          data-testid="upload-error"
          role="alert"
          className="bg-risk-rose/10 border border-risk-rose/30 rounded-lg px-4 py-3 text-sm text-risk-rose"
        >
          Upload failed: {uploadError}
        </div>
      )}

      <div className="text-center">
        <p className="text-xs text-slate-400 mb-3">Don't have data? Try a sample file:</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button variant="ghost" size="sm" onClick={useDemoData}>insurance-renewal-survey.csv</Button>
          <Button variant="ghost" size="sm" onClick={() => simulateUpload('financial-onboarding-survey.csv')}>financial-onboarding-survey.csv</Button>
        </div>
      </div>

      <Card className="bg-soft-panel border-0">
        <p className="text-xs text-slate-500">
          <strong className="text-deep-ink">Supported formats:</strong> CSV (auto-classified), TXT/Markdown (interview transcripts), JSON (structured data), PDF (text extraction only — no layout processing).
        </p>
        <p className="text-xs text-slate-400 mt-1">Demo mode: data is processed locally and never sent to external AI providers.</p>
      </Card>
    </div>
  )
}
